import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  Users, Eye, Activity, Globe, Instagram, Search,
  TrendingUp, Loader2, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  social: { label: 'Social', color: 'bg-pink-100 text-pink-700' },
  search: { label: 'Search', color: 'bg-blue-100 text-blue-700' },
  direct: { label: 'Direct', color: 'bg-slate-100 text-slate-700' },
  referral: { label: 'Referral', color: 'bg-amber-100 text-amber-700' },
};

const AnalyticsWidget = ({ compact = false }) => {
  const { api, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const isAdmin = !!user?.is_admin;

  const fetchAll = async () => {
    try {
      const [sumRes, srcRes] = await Promise.all([
        api.get('/analytics/summary'),
        isAdmin ? api.get('/analytics/sources?days=7').catch(() => null) : Promise.resolve(null),
      ]);
      setAnalytics(sumRes.data);
      setSources(srcRes?.data || null);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAnalytics({
        today: { unique_visitors: 0, page_views: 0, sessions: 0 },
        this_week: { unique_visitors: 0, page_views: 0, sessions: 0 },
        active_users_today: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const doReset = async () => {
    if (!window.confirm('Reset ALL website analytics data? This wipes visits and daily rollups and cannot be undone.')) return;
    setResetting(true);
    try {
      const res = await api.post('/admin/analytics/reset');
      toast.success(`Reset: ${res.data?.deleted?.page_visits || 0} visits, ${res.data?.deleted?.daily_analytics || 0} daily rows`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Visitors Today</p>
                <p className="text-2xl font-bold text-slate-800">{analytics?.today?.unique_visitors || 0}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">This Week</p>
              <p className="text-lg font-semibold text-blue-600">{analytics?.this_week?.unique_visitors || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="analytics-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="w-5 h-5 text-blue-500" />
          Website Analytics
          <span className="ml-2 text-xs font-normal text-slate-400">(Emergent traffic excluded)</span>
          {isAdmin && (
            <button
              onClick={doReset}
              disabled={resetting}
              className="ml-auto text-xs font-normal text-slate-500 hover:text-rose-600 flex items-center gap-1 disabled:opacity-50"
              data-testid="analytics-reset-btn"
              title="Reset all analytics"
            >
              {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reset
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Today's Visitors */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-blue-600 font-medium">TODAY</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{analytics?.today?.unique_visitors || 0}</p>
            <p className="text-xs text-slate-500">Unique Visitors</p>
          </motion.div>

          {/* Page Views */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-teal-600 font-medium">TODAY</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{analytics?.today?.page_views || 0}</p>
            <p className="text-xs text-slate-500">Page Views</p>
          </motion.div>

          {/* Weekly Visitors */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-purple-600 font-medium">THIS WEEK</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{analytics?.this_week?.unique_visitors || 0}</p>
            <p className="text-xs text-slate-500">Total Visitors</p>
          </motion.div>

          {/* Active Users */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-600 font-medium">ACTIVE</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{analytics?.active_users_today || 0}</p>
            <p className="text-xs text-slate-500">Users Online</p>
          </motion.div>
        </div>

        {/* Weekly Summary */}
        <div className="mt-4 p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Weekly Summary</span>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-slate-400" />
                {analytics?.this_week?.page_views || 0} views
              </span>
              <span className="flex items-center gap-1">
                <Activity className="w-3 h-3 text-slate-400" />
                {analytics?.this_week?.sessions || 0} sessions
              </span>
            </div>
          </div>
        </div>

        {/* Traffic sources — admin only */}
        {isAdmin && sources && (
          <div className="mt-4" data-testid="analytics-sources">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Traffic Sources (Last 7 days)</span>
              <span className="text-xs text-slate-400">{sources.total_visits} visits</span>
            </div>
            {sources.by_source && sources.by_source.length > 0 ? (
              <div className="space-y-1.5">
                {sources.by_source.slice(0, 8).map((s) => {
                  const pct = sources.total_visits ? Math.round((s.visits / sources.total_visits) * 100) : 0;
                  const cat = CATEGORY_LABELS[s.source_category] || { label: 'Other', color: 'bg-slate-100 text-slate-600' };
                  return (
                    <div key={`${s.source_name}-${s.source_category}`} className="flex items-center gap-2" data-testid={`source-row-${s.source_name}`}>
                      <div className="w-24 md:w-32 text-xs text-slate-700 truncate flex items-center gap-1">
                        {s.source_category === 'social' && <Instagram className="w-3 h-3 text-pink-500" />}
                        {s.source_category === 'search' && <Search className="w-3 h-3 text-blue-500" />}
                        {s.source_category === 'direct' && <Globe className="w-3 h-3 text-slate-500" />}
                        {s.source_category === 'referral' && <TrendingUp className="w-3 h-3 text-amber-500" />}
                        <span className="truncate">{s.source_name}</span>
                      </div>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            s.source_category === 'social' ? 'bg-pink-400' :
                            s.source_category === 'search' ? 'bg-blue-400' :
                            s.source_category === 'direct' ? 'bg-slate-400' : 'bg-amber-400'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-xs text-slate-600 tabular-nums">
                        {s.visits} <span className="text-slate-400">({pct}%)</span>
                      </div>
                      <span className={`hidden md:inline-block text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${cat.color}`}>{cat.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-xs italic">No traffic yet — share the app on socials or search to start collecting data.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsWidget;
